import { useMemo } from 'react';

import { Link as MuiLink, Typography } from '@mui/material';
import { createLink, useNavigate, useSearch } from '@tanstack/react-router';
import { createColumnHelper } from '@tanstack/react-table';

import createMediaPlanColumn from './createMediaPlanColumn';
import createSelectColumn from './createSelectColumn';
import AlgorithmCell from '../components/cells/AlgorithmCell';
import AlgorithmLevelCell from '../components/cells/AlgorithmLevelCell';
import AlgorithmStatusCell from '../components/cells/AlgorithmStatusCell';
import AlgorithmLevelFilter from '../components/filters/AlgorithmLevelFilter';
import AlgorithmsFilter from '../components/filters/AlgorithmsFilter';
import AlgorithmStatusFilter from '../components/filters/AlgorithmStatusFilter';
import { ALGORITHM_LEVEL } from '../schemas';
import { TraffickingAdvertiserData } from '../types';

const columnHelper = createColumnHelper<TraffickingAdvertiserData>();
const Link = createLink(MuiLink);

const useAdvertiserColumns = () => {
  const columns = useMemo(
    () => [
      createSelectColumn(columnHelper),
      columnHelper.accessor('advertiserName', {
        enableSorting: true,
        header: () => (
          <Typography variant='body3' component='span'>
            Advertiser Name
          </Typography>
        ),
        cell: (props) => (
          <Link
            to='/trafficking/campaign'
            search={{ search: props.row.original.advertiserId }}
            underline='hover'
            color='primary.dark'
            sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
          >
            {props.getValue()}
          </Link>
        ),
        size: 400,
      }),
      columnHelper.accessor('advertiserId', {
        enableSorting: true,
        header: () => (
          <Typography variant='body3' component='span'>
            Advertiser ID
          </Typography>
        ),
        cell: (props) => (
          <Typography
            variant='subtitleNumbers'
            component='span'
            color='text.tertiary'
          >
            {props.getValue()}
          </Typography>
        ),
        size: 160,
      }),
      columnHelper.accessor('algorithms', {
        id: 'algorithms',
        enableSorting: true,
        sortDescFirst: false,
        header: () => (
          <Typography variant='body3' component='span'>
            Algorithms
          </Typography>
        ),
        cell: (props) => (
          <AlgorithmCell algorithmsStatus={props.row.original.algorithms} />
        ),
        size: 180,
        meta: {
          FilterComponentFn: (_column) => {
            const searchParams = useSearch({
              from: '/_app/trafficking/advertiser',
            });
            const navigate = useNavigate({ from: '/trafficking/advertiser' });

            return (
              <AlgorithmsFilter
                filterId='algorithmsFilter'
                buttonAriaLabel='algorithms filter'
                searchParamAlgorithmIds={searchParams.algorithmId}
                handleNavigation={(newAlgos) => {
                  void navigate({
                    search: {
                      ...searchParams,
                      algorithmId: newAlgos,
                      page: 1,
                    },
                    resetScroll: false,
                  });
                }}
              />
            );
          },
        },
      }),
      columnHelper.accessor('algorithmStatus', {
        id: 'algorithmStatus',
        enableSorting: true,
        sortDescFirst: false,
        header: () => (
          <Typography variant='body3' component='span'>
            Status
          </Typography>
        ),
        cell: (props) => (
          <AlgorithmStatusCell
            algorithmsStatus={props.row.original.algorithms}
          />
        ),
        size: 200,
        meta: {
          FilterComponentFn: (_column) => {
            const searchParams = useSearch({
              from: '/_app/trafficking/advertiser',
            });
            const navigate = useNavigate({ from: '/trafficking/advertiser' });

            return (
              <AlgorithmStatusFilter
                filterId='algorithmStatusFilter'
                buttonAriaLabel='algorithm status filter'
                searchParamAlgorithmStatuses={searchParams.status}
                handleNavigation={(selectedAlgoStatuses) => {
                  void navigate({
                    search: {
                      ...searchParams,
                      status: selectedAlgoStatuses,
                      page: 1,
                    },
                    resetScroll: false,
                  });
                }}
              />
            );
          },
        },
      }),
      columnHelper.display({
        id: 'algorithmLevel',
        enableSorting: false,
        header: () => (
          <Typography variant='body3' component='span'>
            Level
          </Typography>
        ),
        cell: (props) => (
          <AlgorithmLevelCell
            algorithmsStatus={props.row.original.algorithms}
          />
        ),
        size: 180,
        meta: {
          FilterComponentFn: (_column) => {
            const searchParams = useSearch({
              from: '/_app/trafficking/advertiser',
            });
            const navigate = useNavigate({ from: '/trafficking/advertiser' });

            return (
              <AlgorithmLevelFilter
                level={ALGORITHM_LEVEL.ADVERTISER}
                filterId='algorithmLevelFilter'
                buttonAriaLabel='algorithm level filter'
                searchParamAlgorithmLevels={searchParams.level}
                handleNavigation={(selectedAlgoLevels) => {
                  void navigate({
                    search: {
                      ...searchParams,
                      level: selectedAlgoLevels,
                      page: 1,
                    },
                    resetScroll: false,
                  });
                }}
              />
            );
          },
        },
      }),
      createMediaPlanColumn(columnHelper),
    ],
    []
  );

  return columns;
};

export default useAdvertiserColumns;
